import { useState, useEffect } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import moment from 'moment'
import _ from 'lodash'

import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import LowPriorityIcon from '@mui/icons-material/LowPriority'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CachedIcon from '@mui/icons-material/Cached'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { Card } from '@mui/material'

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import { constants as cons } from '../../Constants/constants'
import { getOne, put, post } from '../../helper/baseFetch'
import { setQueueStatus, setQueueStatusValue, setQueueStatusResourcesStepsValue } from '../../actions/queueStatusAction'
import { EditCreateDisplay } from '../../Component/EditCreateDisplay/EditCreateDisplay'
import { TempTextInput } from '../../Component/Input/TempTextInput'
import { TempBtn } from '../../Component/Input/TempBtn'
import { TempAutoSelectInput } from '../../Component/Input/TempAutoSelectInput'
import { getString, helperTextStyles } from '../../helper/util'
import { GlobalDialog } from '../../Component/GlobalDialog'
import { ResourcesSteps } from './ResourcesSteps'
import { ResetDialog } from './ResetDialog'
import { CounterProfileSelectField } from './CounterProfileSelectField'
import useRestaurant from '../../hooks/Restaurant/useRestaurant'
import { BOOLEAN_OPTIONS } from '../../Constants/type'
import { Clear } from '@mui/icons-material'
import { getContent } from '../../Component/EditCreateDisplay/EditUtil'

export const QueueStatusEdit = () => {
  const dispatch = useDispatch()
  const store = useStore()
  const history = useHistory()
  const params = useParams()
  const [ready, setReady] = useState(false)
  const [clear, setClear] = useState(true)
  const [dialog, setDialog] = useState({
    open: false,
    dialogActions: [],
  })

  const defaultError = { transferable: '', checkOutType: '', maxTicketPerSection: '', limitTicketInMinute: '' }
  const [error, setError] = useState(defaultError)
  const helperTextStylesClasses = helperTextStyles()
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const resource = 'QueueStatus'
  const savedQueueStatus = useSelector((state) => state.queueStatus.ui.queueStatus)
  const lang = useSelector((state) => state.lang.ui.lang)
  const theme = useSelector((state) => state.theme.ui.theme)
  const { getRestaurantIdAndName } = useRestaurant(params.id)

  const fields = [
    {
      type: cons.TYPE.TITLE,
      value: { resource: resource, key: 'queueChannel' },
      className: 'sub-title',
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'mobileAvailable' },
      value: savedQueueStatus.mobileAvailable,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_STATUS.COMMON_BOOLEAN,
      disableClearable: true,
      name: 'mobileAvailable',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'mobileTvAvailable' },
      value: savedQueueStatus.mobileTvAvailable,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_STATUS.COMMON_BOOLEAN,
      disableClearable: true,
      name: 'mobileTvAvailable',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'webAvailable' },
      value: savedQueueStatus.webAvailable,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_STATUS.COMMON_BOOLEAN,
      disableClearable: true,
      name: 'webAvailable',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'webExternalAvailable' },
      value: savedQueueStatus.webExternalAvailable,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_STATUS.COMMON_BOOLEAN,
      disableClearable: true,
      name: 'webExternalAvailable',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'webTvAvailable' },
      value: savedQueueStatus.webTvAvailable,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_STATUS.COMMON_BOOLEAN,
      disableClearable: true,
      name: 'webTvAvailable',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'webTvExternalAvailable' },
      value: savedQueueStatus.webTvExternalAvailable,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_STATUS.COMMON_BOOLEAN,
      disableClearable: true,
      name: 'webTvExternalAvailable',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'wechatMpAvailable' },
      value: savedQueueStatus.wechatMpAvailable,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_STATUS.COMMON_BOOLEAN,
      disableClearable: true,
      name: 'wechatMpAvailable',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'lineAvailable' },
      value: savedQueueStatus.lineAvailable,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_STATUS.COMMON_BOOLEAN,
      disableClearable: true,
      name: 'lineAvailable',
      isEndOfLine: false,
    },
    {
      type: cons.TYPE.TITLE,
      value: { resource: resource, key: 'dailyReset' },
      className: 'sub-title',
    },
    {
      className: 'edit-create-input',
      variant: cons.STYLE.VARIANT.STANDARD,
      label: { key: 'resetUpdateTimestamp' },
      value: savedQueueStatus.resetUpdateTimestamp ? moment(savedQueueStatus.resetUpdateTimestamp).format('YYYY-MM-DD HH:mm:ss') : '',
      name: 'resetUpdateTimestamp',
      isEndOfLine: true,
      disabled: true,
    },
    {
      className: 'edit-create-input',
      type: cons.TYPE.TIME,
      value: savedQueueStatus.dailyResetTime,
      label: { resource: resource, key: 'dailyResetTime' },
      name: 'dailyResetTime',
      clear: true,
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'resetQueueStatus' },
      value: savedQueueStatus.resetQueueStatus,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_CONFIG.RESET_QUEUE_STATUS,
      disableClearable: true,
      name: 'resetQueueStatus',
      isEndOfLine: false,
    },
    {
      className: 'edit-create-input',
      label: { resource: resource, key: 'dailyResetType' },
      value: savedQueueStatus.dailyResetType,
      type: cons.TYPE.SELECT,
      selectValue: cons.CHOICES.QUEUE_CONFIG.DAILY_RESET_TYPE,
      disableClearable: true,
      name: 'dailyResetType',
      isEndOfLine: true,
    },
    {
      label: { resource: resource, key: 'autoTask' },
      value: savedQueueStatus.autoTask,
      type: cons.TYPE.SELECT,
      selectValue: BOOLEAN_OPTIONS,
      disableClearable: true,
      name: 'autoTask',
      isEndOfLine: false,
    },
    {
      label: { resource: resource, key: 'autoSwitchTimeSection' },
      value: savedQueueStatus.autoSwitchTimeSection,
      type: cons.TYPE.SELECT,
      selectValue: BOOLEAN_OPTIONS,
      name: 'autoSwitchTimeSection',
      disableClearable: true,
      isEndOfLine: false,
    },
    {
      label: { resource: resource, key: 'autoRequestTicketStatus' },
      value: savedQueueStatus.autoRequestTicketStatus,
      type: cons.TYPE.SELECT,
      selectValue: BOOLEAN_OPTIONS,
      disableClearable: true,
      name: 'autoRequestTicketStatus',
      isEndOfLine: false,
    },
  ]
  const content = [
    {
      tab: fields.map((inputSetting, index) => {
        let content = getContent(inputSetting, index, onChangeInput)
        return (
          <div className="edit-display-wrapper" key={index}>
            {content}
            {inputSetting.isEndOfLine && <div></div>}
          </div>
        )
      }),
      btn: true,
    },
  ]

  const bottomBtns = [
    {
      variant: cons.STYLE.VARIANT.CONTAINED,
      color: cons.STYLE.PRIMARY,
      label: { key: 'save' },
      onClick: onClickSave,
      className: 'margin-left-16',
      icon: <SaveIcon />,
    },
    {
      variant: cons.STYLE.VARIANT.CONTAINED,
      color: cons.STYLE.PRIMARY,
      label: { key: 'clear' },
      disabled: false,
      onClick: () => setClear(true),
      className: 'margin-left-16',
      icon: <ClearIcon />,
    },
    {
      className: 'special-button special-button-margin-left',
      variant: cons.STYLE.VARIANT.CONTAINED,
      icon: <CachedIcon />,
      label: { resource: resource, key: 'reset' },
      onClick: onClickClearDialog,
    },
  ]

  const onClickBackToEdit = () => {
    setDialog({
      open: false,
      dialogActions: [],
    })
    setClear(true)
  }

  const onClickBackToList = () => {
    setDialog({
      open: false,
      dialogActions: [],
    })
    history.push('/' + cons.PATH.SHOP.SYSTEM_GROUP_RESTAURANT)
  }

  const setUnlimitedQuota = (name) => {
    onChangeInput(name, null)
  }

  const setNull = (name) => {
    dispatch(setQueueStatusValue(name, savedQueueStatus, '', null))
  }

  function onDragEnd(result) {
    if (!result.destination) return
    let newQueueSteps = _.cloneDeep(savedQueueStatus.queueSteps)
    const [reorderedItem] = newQueueSteps.splice(result.source.index, 1)
    newQueueSteps.splice(result.destination.index, 0, reorderedItem)
    dispatch(setQueueStatusValue('queueSteps', savedQueueStatus, '', newQueueSteps))
  }

  function addQueueSteps() {
    let newQueueSteps = _.cloneDeep(savedQueueStatus.queueSteps)
    newQueueSteps && newQueueSteps.length > 0 ? newQueueSteps.push('') : (newQueueSteps = [''])
    dispatch(setQueueStatusValue('queueSteps', savedQueueStatus, '', newQueueSteps))
  }

  function removeQueueSteps(idx) {
    let newQueueSteps = _.cloneDeep(savedQueueStatus.queueSteps)
    if (newQueueSteps && newQueueSteps.length > 0) {
      newQueueSteps = newQueueSteps.filter((item, newQueueStepsIndex) => newQueueStepsIndex !== idx)
    }
    dispatch(setQueueStatusValue('queueSteps', savedQueueStatus, '', newQueueSteps))
  }

  function validate(savedQueueConfig) {
    let newError = { ...error }

    if (
      (Number.isInteger(savedQueueConfig.limitTicketInMinute) && Number.isInteger(savedQueueConfig.maxTicketPerSection)) ||
      (savedQueueConfig.limitTicketInMinute === null && savedQueueConfig.maxTicketPerSection === null)
    ) {
      newError.maxTicketPerSection = ''
      newError.limitTicketInMinute = ''
    } else {
      newError.maxTicketPerSection = 'Both Max Ticket Per Section and Limit Ticket In Minute should be integer or empty.'
      newError.limitTicketInMinute = 'Both Max Ticket Per Section and Limit Ticket In Minute should be integer or empty.'
    }

    if (savedQueueConfig.transferable && savedQueueConfig.checkOutType === 'NONE') {
      newError.transferable = 'Check Out Type Should Not be NONE if transferrable is true'
      newError.checkOutType = 'Check Out Type Should Not be NONE if transferrable is true'
    } else {
      newError.transferable = ''
      newError.checkOutType = ''
    }

    setError(newError)
  }

  function onChangeInput(name, e, idx) {
    if (e === '') {
      e = null
    }

    switch (name) {
      case 'campaignStartTimestamp':
      case 'campaignEndTimestamp':
        dispatch(setQueueStatusValue(name, savedQueueStatus, '', e ? Number(moment(e).format('x')) : null))
        break
      case 'timeSectionQuota':
      case 'rollTagShiftValue':
      case 'ticketExpiredMinute':
      case 'checksumExpiryLimit':
      case 'queuePeriod':
      case 'minCheckInSize':
      case 'maxCheckInSize':
      case 'checkInLateMinute':
      case 'checkInBeforeMinute':
        //TO-DO
        //a quick fix,
        // when user input invalid number, the input will fixed as 0
        // user input 0.1 > fixed to 0
        // second time user input 0.2 > the non-change 0 does not trigger useState change
        // causing the input field displaying 0.2
        dispatch(setQueueStatusValue(name, savedQueueStatus, '', e))
        dispatch(setQueueStatusValue(name, savedQueueStatus, '', e === '-' || !e ? e : Number.isInteger(Number(e)) ? parseInt(e) : 0))
        break
      case 'queueSteps':
        let newQueueSteps = _.cloneDeep(savedQueueStatus.queueSteps)
        newQueueSteps = newQueueSteps.map((newQueueStepsValue, newQueueStepsIndex) => {
          if (newQueueStepsIndex === idx) {
            newQueueStepsValue = e
          }
          return newQueueStepsValue
        })
        dispatch(setQueueStatusValue(name, savedQueueStatus, '', newQueueSteps))
        break
      case 'dailyResetTime':
        if (!e) {
          dispatch(setQueueStatusValue(name, savedQueueStatus, '', e))
          return
        }

        let time = e
        const timeRegex = /^[0-9]{2}:[0-9]{2}$/
        if (time.match(timeRegex)) {
          time = time + ':00'
        }
        dispatch(setQueueStatusValue(name, savedQueueStatus, '', time))
        break
      case 'resourcesSteps':
        dispatch(setQueueStatusResourcesStepsValue(name, savedQueueStatus, '', e, idx))
        break
      case 'transferable':
      case 'checkOutType':
        dispatch(setQueueStatusValue(name, savedQueueStatus, '', e))
        break
      case 'maxTicketPerSection':
      case 'limitTicketInMinute':
        dispatch(setQueueStatusValue(name, savedQueueStatus, '', Number(e)))
        break
      default:
        dispatch(setQueueStatusValue(name, savedQueueStatus, '', e))
        break
    }
  }

  function onClickClearDialog() {
    setResetDialogOpen(true)
  }

  function onClickReset() {
    put(
      cons.PATH.SHOP.QUEUE.RESET_CURRENT_TIME_SECTION + '/' + params.id,
      undefined,
      () => {
        setDialog({
          open: true,
          onClose: onClickBackToEdit,
          dialogContentText: { key: 'resetSuccess' },
          dialogActions: [
            { label: { key: 'backEdit' }, onClick: onClickBackToEdit, icon: <ArrowBackIcon /> },
            { label: { key: 'backList' }, onClick: onClickBackToList, icon: <LowPriorityIcon /> },
          ],
        })
        setClear(false)
      },
      undefined,
      store
    )
  }

  function onClickSave() {
    let newSavedQueueConfig = _.cloneDeep(savedQueueStatus)

    if (Object.values(error).some((value) => value)) {
      return
    }

    setError(defaultError)

    let newCustomData = []

    if (newSavedQueueConfig.email) {
      newCustomData.push({ type: 'EMAIL', required: true })
    }
    if (newSavedQueueConfig.mobile) {
      newCustomData.push({ type: 'MOBILE', required: true })
    }

    delete newSavedQueueConfig.resetUpdateTimestamp

    if (params.id) {
      put(
        cons.PATH.SHOP.QUEUE.QUEUE_STATUS + '/' + params.id,
        newSavedQueueConfig,
        () => {
          setDialog({
            open: true,
            onClose: onClickBackToEdit,
            dialogContentText: { key: 'editSuccess' },
            dialogActions: [
              { label: { key: 'backEdit' }, onClick: onClickBackToEdit, icon: <ArrowBackIcon /> },
              { label: { key: 'backList' }, onClick: onClickBackToList, icon: <LowPriorityIcon /> },
            ],
          })
          setClear(false)
        },
        undefined,
        store
      )
      // console.log(tempSavedQueueConfig)
    } else {
      newSavedQueueConfig.restUrlId = params.id
      post(
        cons.PATH.SHOP.QUEUE.QUEUE_STATUS,
        newSavedQueueConfig,
        () => {
          setDialog({
            open: true,
            onClose: onClickBackToEdit,
            dialogContentText: { key: 'editSuccess' },
            dialogActions: [
              { label: { key: 'backEdit' }, onClick: onClickBackToEdit, icon: <ArrowBackIcon /> },
              { label: { key: 'backList' }, onClick: onClickBackToList, icon: <LowPriorityIcon /> },
            ],
          })
          setClear(false)
        },
        undefined,
        store
      )
    }
  }

  useEffect(() => {
    if (clear === true) {
      setError(defaultError)
      getOne(
        cons.PATH.SHOP.QUEUE.QUEUE_STATUS + '/' + params.id,
        undefined,
        (payload) => {
          let newPayload = _.cloneDeep(payload)


          dispatch(setQueueStatus(newPayload))
          setReady(true)
          setClear(false)
        },
        undefined,
        store
      )
    }
  }, [clear]) // eslint-disable-line react-hooks/exhaustive-deps


  return (
    ready && (
      <>
        <EditCreateDisplay
          code={params.id ? params.id : ''}
          resource={resource}
          tabs={false}
          content={content}
          onChangeInput={onChangeInput}
          bottomBtns={bottomBtns}
          display="none"
        />
        {dialog && dialog.open && <GlobalDialog dialogInfo={dialog} />}
        {resetDialogOpen && (
          <ResetDialog
            open={resetDialogOpen}
            setOpen={setResetDialogOpen}
            onClickReset={onClickReset}
            resetTimeStamp={savedQueueStatus.resetUpdateTimestamp}
            shopName={getRestaurantIdAndName(params.id)}
          />
        )}
      </>
    )
  )
}
